import { useState } from 'react';
import { UserPlus, Mail, MoreHorizontal, Pencil, Trash2, ShieldOff } from 'lucide-react';
import {
  useSuperAdmins,
  useInviteSuperAdmin,
  useResendSuperAdminInvite,
  useUpdateAdmin,
  useDeactivateAdmin,
  useReset2FA,
} from '@/hooks/api/useSuperAdmins';
import { useAuthStore } from '@/store/authStore';
import { useDebounce, useUrlTable } from '@/hooks';
import { SuperAdmin } from '@/api/superadmins';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { InviteSuperAdminDialog } from '@/components/dialogs/InviteSuperAdminDialog';
import { ConfirmDialog } from '@/components/dialogs/ConfirmDialog';
import { EditSuperAdminDialog } from '@/components/dialogs/EditSuperAdminDialog';
import { formatDate } from '@/lib/date';

interface TableState {
  page: number;
  limit: number;
  search?: string;
}

export default function SuperAdminsList() {
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const { state, setPage, setSearch } = useUrlTable<TableState>({
    page: 1,
    limit: 25,
    search: '',
  });
  const [selectedAdmin, setSelectedAdmin] = useState<SuperAdmin | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    description: string;
    onConfirm: () => void;
    variant?: 'default' | 'destructive';
  }>({
    open: false,
    title: '',
    description: '',
    onConfirm: () => {},
  });

  const debouncedSearch = useDebounce(state.search, 300);

  const { superAdmin: currentUser } = useAuthStore();
  const { data, isLoading } = useSuperAdmins({
    page: state.page,
    limit: state.limit,
    search: debouncedSearch || undefined,
  });
  const inviteMutation = useInviteSuperAdmin();
  const resendMutation = useResendSuperAdminInvite();
  const updateAdminMutation = useUpdateAdmin();
  const deactivateMutation = useDeactivateAdmin();
  const reset2FAMutation = useReset2FA();

  const handleInvite = (email: string) => {
    inviteMutation.mutate(
      { email },
      {
        onSuccess: () => setInviteDialogOpen(false),
      },
    );
  };

  const handleResendInvite = (email: string) => {
    setConfirmDialog({
      open: true,
      title: 'Resend Invitation',
      description: `Resend invitation email to ${email}?`,
      onConfirm: () => {
        resendMutation.mutate(email, {
          onSuccess: () => setConfirmDialog((prev) => ({ ...prev, open: false })),
        });
      },
    });
  };

  const handleEditAdmin = (admin: SuperAdmin) => {
    setSelectedAdmin(admin);
    setEditDialogOpen(true);
  };

  const handleUpdateAdmin = (name: string) => {
    if (!selectedAdmin) return;
    updateAdminMutation.mutate(
      { id: selectedAdmin._id, payload: { name } },
      {
        onSuccess: () => {
          setEditDialogOpen(false);
          setSelectedAdmin(null);
        },
      },
    );
  };

  const handleDeactivateAdmin = (admin: SuperAdmin) => {
    setConfirmDialog({
      open: true,
      title: 'Deactivate Super Admin',
      description: `Are you sure you want to deactivate ${admin.name || admin.email}? They will no longer be able to access the super admin portal.`,
      onConfirm: () => {
        deactivateMutation.mutate(admin._id, {
          onSuccess: () => setConfirmDialog((prev) => ({ ...prev, open: false })),
        });
      },
      variant: 'destructive',
    });
  };

  const handleReset2FA = (admin: SuperAdmin) => {
    setConfirmDialog({
      open: true,
      title: 'Reset 2FA',
      description: `Are you sure you want to reset 2FA for ${admin.name || admin.email}? They will need to set up 2FA again on their next login.`,
      onConfirm: () => {
        reset2FAMutation.mutate(admin._id, {
          onSuccess: () => setConfirmDialog((prev) => ({ ...prev, open: false })),
        });
      },
    });
  };

  // Check if admin is the current user (can't edit/deactivate yourself)
  const isCurrentUser = (admin: SuperAdmin) => admin._id === currentUser?._id;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Super Admins</h1>
          <p className="text-muted-foreground mt-1">Manage super administrator accounts</p>
        </div>
        <Button onClick={() => setInviteDialogOpen(true)}>
          <UserPlus className="h-4 w-4 mr-2" />
          Invite Admin
        </Button>
      </div>

      <div className="bg-card rounded-lg shadow-sm border border-border">
        <div className="p-4 border-b border-border">
          <Input
            placeholder="Search by name or email..."
            value={state.search || ''}
            onChange={(e) => setSearch(e.target.value)}
            className="max-w-sm"
          />
        </div>

        {isLoading ? (
          <div className="p-4 space-y-3">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        ) : !data || data.data.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-muted-foreground">No super admins found</p>
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>2FA</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="w-[70px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.data.map((admin) => (
                  <TableRow key={admin._id}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {admin.name || 'N/A'}
                        {isCurrentUser(admin) && (
                          <Badge variant="default" className="text-xs">
                            You
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{admin.email}</TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          admin.status === 'active'
                            ? 'active'
                            : admin.status === 'deactivated'
                              ? 'error'
                              : 'pending'
                        }
                      >
                        {admin.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={admin.twoFactorEnabled ? 'active' : 'default'}>
                        {admin.twoFactorEnabled ? 'Enabled' : 'Disabled'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(admin.createdAt)}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {admin.status === 'pending' && (
                            <>
                              <DropdownMenuItem
                                onClick={() => handleResendInvite(admin.email)}
                                disabled={resendMutation.isPending}
                              >
                                <Mail className="h-4 w-4 mr-2" />
                                Resend Invite
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                            </>
                          )}
                          {!isCurrentUser(admin) && admin.status === 'active' && (
                            <>
                              <DropdownMenuItem onClick={() => handleEditAdmin(admin)}>
                                <Pencil className="h-4 w-4 mr-2" />
                                Edit Name
                              </DropdownMenuItem>
                              {admin.twoFactorEnabled && (
                                <DropdownMenuItem onClick={() => handleReset2FA(admin)}>
                                  <ShieldOff className="h-4 w-4 mr-2" />
                                  Reset 2FA
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem
                                onClick={() => handleDeactivateAdmin(admin)}
                                className="text-red-600"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Deactivate
                              </DropdownMenuItem>
                            </>
                          )}
                          {isCurrentUser(admin) && (
                            <DropdownMenuItem disabled>
                              <span className="text-muted-foreground text-sm">
                                Use Account settings to edit your profile
                              </span>
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {data && (
              <div className="flex items-center justify-between border-t border-border px-4 py-3">
                <div className="text-sm text-muted-foreground">
                  {data.totalPages > 1
                    ? `Page ${data.page} of ${data.totalPages}`
                    : `Showing ${data.total} ${data.total === 1 ? 'admin' : 'admins'}`}
                </div>
                {data.totalPages > 1 && (
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(data.page - 1)}
                      disabled={data.page === 1}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(data.page + 1)}
                      disabled={data.page === data.totalPages}
                    >
                      Next
                    </Button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      <InviteSuperAdminDialog
        open={inviteDialogOpen}
        onOpenChange={setInviteDialogOpen}
        onInvite={handleInvite}
        loading={inviteMutation.isPending}
      />

      <EditSuperAdminDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        onSave={handleUpdateAdmin}
        admin={selectedAdmin}
        loading={updateAdminMutation.isPending}
      />

      <ConfirmDialog
        open={confirmDialog.open}
        onOpenChange={(open) => setConfirmDialog({ ...confirmDialog, open })}
        title={confirmDialog.title}
        description={confirmDialog.description}
        onConfirm={confirmDialog.onConfirm}
        variant={confirmDialog.variant}
        loading={
          deactivateMutation.isPending || reset2FAMutation.isPending || resendMutation.isPending
        }
      />
    </div>
  );
}
